// Tenantra Inc. - Payments > Transactions: every tenant payment and payout in a month
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { TableSkeleton, exportCsv, ExportButton, useSort, RowOpenButton } from './TableKit';
import { LightHeader, StatStrip, Pill } from './ScreeningAddresses';
import { fmtDate } from './DetailPanel';
import { money, PAY_STATUS, METHOD } from './payments';

const COLUMNS = [
  { key: 'date', label: 'Date', span: 2 }, { key: 'who', label: 'Tenant', span: 3 }, { key: 'type', label: 'Type', span: 2 },
  { key: 'method', label: 'Method', span: 1 }, { key: 'amount', label: 'Amount', span: 1 }, { key: 'status', label: 'Status', span: 2 }, { key: 'open', label: '', span: 1, sortable: false },
];
const kindOf = (r) => (r.kind === 'payout' ? 'payout' : r.label === 'Rent' ? 'rent' : 'charge');
const signed = (r) => (r.kind === 'payout' ? -r.amount : r.amount);
const ACC = { date: r => r.created_at, who: r => r.tenant_name || 'Payout', type: r => r.label, method: r => METHOD[r.method] || '', amount: signed, status: r => r.status };
const who = (r) => (r.kind === 'payout' ? 'Payout to your bank' : r.tenant_name);
const where = (r) => (r.kind === 'payout' ? r.destination || '' : `${r.unit ? `Unit ${r.unit}, ` : ''}${r.address}`);
const amountText = (r) => `${r.kind === 'payout' ? '−' : '+'}${money(r.amount)}`;

function TxnRow({ r, onOpen }) {
  const st = PAY_STATUS[r.status] || [r.status, 'slate'];
  const canOpen = r.kind === 'payment';
  return (
    <div onClick={canOpen ? onOpen : undefined} className={`grid grid-cols-12 gap-3 px-5 py-3 items-center transition-colors ${canOpen ? 'cursor-pointer hover:bg-slate-50/80' : ''}`} data-testid={`txn-row-${r.id}`}>
      <div className="hidden sm:block col-span-2 text-sm text-slate-700">{fmtDate(r.created_at)}</div>
      <div className="col-span-7 sm:col-span-3 min-w-0"><p className={`text-sm truncate ${canOpen ? 'text-teal-700' : 'text-slate-800'}`}>{who(r)}</p><p className="text-xs text-slate-500 truncate">{where(r)}</p>
        <p className="sm:hidden text-xs text-slate-500 mt-0.5">{r.label} · {fmtDate(r.created_at)}{r.method ? ` · ${METHOD[r.method] || r.method}` : ''}</p></div>
      <div className="hidden sm:block col-span-2 text-sm text-slate-700 truncate" data-testid={`txn-type-${r.id}`}>{r.label}</div>
      <div className="hidden sm:block col-span-1 text-xs text-slate-600">{METHOD[r.method] || '–'}</div>
      <div className="hidden sm:block col-span-1 min-w-0"><p className={`text-sm tabular-nums ${r.kind === 'payout' ? 'text-slate-500' : 'text-slate-800'}`} data-testid={`txn-amount-${r.id}`}>{amountText(r)}</p>{r.fee > 0 && <p className="text-[11px] text-slate-400 tabular-nums">fee {money(r.fee)}</p>}</div>
      <div className="col-span-5 sm:col-span-2"><p className="sm:hidden text-sm tabular-nums text-slate-800 mb-0.5">{amountText(r)}</p><Pill tone={st[1]} testId={`txn-status-${r.id}`}>{st[0]}</Pill></div>
      <div className="hidden sm:flex col-span-1 justify-end">{canOpen && <RowOpenButton onClick={onOpen} testId={`txn-open-${r.id}`} label={`Open payment from ${r.tenant_name}`} />}</div>
    </div>
  );
}

export function TransactionsView({ month, head, onOpen }) {
  const [data, setData] = useState(null);
  const [type, setType] = useState('');
  useEffect(() => {
    let live = true;
    setData(null);
    api.get('/landlord/transactions', { params: { month } }).then(r => live && setData(r.data)).catch(e => live && toast.error(apiHelpers.handleError(e)));
    return () => { live = false; };
  }, [month]);
  const rows = useMemo(() => (data?.rows || []).filter(r => !type || kindOf(r) === type), [data, type]);
  const { sorted, sort, toggle } = useSort(rows, ACC);
  const t = data?.totals || {};
  const exportRows = () => exportCsv(`transactions-${month}.csv`, sorted, [
    { label: 'Date', value: r => r.created_at.slice(0, 10) }, { label: 'Tenant', value: r => r.tenant_name || '' }, { label: 'Property', value: r => r.address || '' },
    { label: 'Unit', value: r => r.unit || '' }, { label: 'Type', value: r => r.label }, { label: 'Method', value: r => METHOD[r.method] || '' },
    { label: 'Amount', value: signed }, { label: 'Fee', value: r => r.fee || 0 }, { label: 'Status', value: r => (PAY_STATUS[r.status] || [r.status])[0] }, { label: 'Reference', value: r => r.reference || '' }]);
  return (
    <>
      <StatStrip tiles={[
        { id: 'received', label: 'Received', value: data ? money(t.received) : '–' }, { id: 'txn-progress', label: 'In progress', value: data ? money(t.in_progress) : '–' },
        { id: 'returned', label: 'Returned or failed', value: data ? money(t.returned) : '–' }, { id: 'paid-out', label: 'Paid out', value: data ? money(t.paid_out) : '–' }]} />
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="txn-table">
        <div className="flex flex-wrap items-center gap-2.5 px-5 py-3">
          {head}
          <select value={type} onChange={(e) => setType(e.target.value)} className="h-8 px-2 text-xs bg-white border border-slate-200 rounded-md text-slate-700" data-testid="txn-filter-type">
            <option value="">All types</option><option value="rent">Rent</option><option value="charge">Charges</option><option value="payout">Payouts</option>
          </select>
          <ExportButton onClick={exportRows} disabled={!sorted.length} testId="txn-export" />
        </div>
        {!data ? <div className="px-5 pb-4"><TableSkeleton rows={5} spans={[2, 3, 2, 1, 1, 2, 1]} /></div> : sorted.length === 0 ? (
          <p className="px-5 py-8 border-t border-slate-100 text-sm text-slate-500 text-center" data-testid="txn-empty">No transactions this month</p>
        ) : <>
          <LightHeader columns={COLUMNS} sort={sort} onSort={toggle} />
          <div className="divide-y divide-slate-100">{sorted.map(r => <TxnRow key={r.id} r={r} onOpen={() => onOpen(r.id)} />)}</div>
          <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-500" data-testid="txn-footer">Showing {sorted.length} of {data.rows.length} transactions</div>
        </>}
      </div>
    </>
  );
}
